import { useEffect, type RefObject } from 'react';

/**
 * Close a popover on a click outside it or on Escape.
 *
 * Extracted from StatusFilter, which had the only copy, once the task list
 * needed a second and third one. Listens on the document rather than wrapping
 * the page in a click-catcher: a scrim would have to sit above the row it is
 * anchored in, and then the trigger itself is unclickable.
 *
 * `mousedown`, not `click`: a click that starts inside the panel and ends
 * outside it -- dragging across a date field -- is not a dismissal.
 *
 * Escape is marked handled, which StatusFilter never needed because it does not
 * live inside the task panel. Anything rendered inside SidePanel has to say so,
 * or one Escape closes the popover and the whole panel behind it (see the
 * `defaultPrevented` check in SidePanel).
 */
export function useDismiss(
  ref: RefObject<HTMLElement | null>,
  open: boolean,
  onDismiss: () => void,
): void {
  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: MouseEvent) {
      if (!ref.current?.contains(event.target as Node)) onDismiss();
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      onDismiss();
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [ref, open, onDismiss]);
}
